import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Button } from './Button'
import { Card, CardBody } from './Card'
import { DateField } from './DateField'
import { Input } from './Input'
import { Textarea } from './Textarea'
import { Switch } from './Switch'
import { Spinner } from './Spinner'
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure } from './Modal'
import { compressImage } from '../utils/compressImage'

// A capped, member-safe preview of who joined an event. Deliberately narrower
// than the staff-only attendee list: no email, no legal last name.
export interface NewsAttendee {
  user_id: string
  /** Nickname, else first name. */
  name: string
  avatar_url: string | null
}

export interface NewsItem {
  id: string
  gym_id: string | null
  title: string
  description: string
  image_url: string | null
  link: string | null
  is_event: boolean
  news_date: string
  seats?: number | null
  starts_at?: string | null
  ends_at?: string | null
  rsvp_enabled?: boolean
  is_app_update?: boolean
  published?: boolean
  signup_count?: number
  joined?: boolean
  /** Public attendee preview for a gym event. Never set on global items. */
  attendees?: NewsAttendee[]
}

// Formats an event's stored wall-clock start/end ("YYYY-MM-DDTHH:MM[:SS]") into
// a compact label. Same-day: "Aug 23, 18:00–20:00"; multi-day shows both dates.
export function formatEventRange(
  startsAt: string | null | undefined,
  endsAt: string | null | undefined,
  locale?: string,
): string {
  if (!startsAt) return ''
  const fmtDate = (iso: string) =>
    new Date(iso.length <= 16 ? `${iso}:00` : iso).toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
    })
  const time = (iso: string) => iso.slice(11, 16)
  const startDate = startsAt.slice(0, 10)
  if (!endsAt) return `${fmtDate(startsAt)}, ${time(startsAt)}`
  if (startDate === endsAt.slice(0, 10)) {
    return `${fmtDate(startsAt)}, ${time(startsAt)}–${time(endsAt)}`
  }
  return `${fmtDate(startsAt)} ${time(startsAt)} – ${fmtDate(endsAt)} ${time(endsAt)}`
}

// Translation function, supplied by the host app so this shared component uses
// the app's own initialized i18n instance and `news.*` keys.
type TFunc = (key: string, opts?: Record<string, unknown>) => string

interface NewsManagerProps {
  apiUrl: string
  getToken: () => Promise<string>
  t: TFunc
  /** 'gym' authors gym-scoped news; 'global' authors super-admin global news. */
  scope: 'gym' | 'global'
  /** Required when scope is 'gym' — the gym whose news is managed. */
  gymId?: string
  /**
   * Host-provided attendee list, expanded inline under an event row. The gym app
   * owns it so attendees render as real member profiles; keeping it inline (rather
   * than in a modal) means opening a profile doesn't stack two modals.
   */
  renderAttendees?: (newsId: string) => ReactNode
  /**
   * Replaces the built-in title row. The gym app passes its own page header,
   * which puts "Add" in the phone's top bar as a "+".
   */
  renderHeader?: (openCreate: () => void) => ReactNode
}

/** "2026-11-02" → "02.11.2026": day first, like every other date in the apps. */
function dayFirst(isoDate: string): string {
  const [y, m, d] = isoDate.slice(0, 10).split('-')
  return y && m && d ? `${d}.${m}.${y}` : isoDate
}

const MAX_IMAGE_BYTES = 5 * 1024 * 1024

interface FormState {
  title: string
  description: string
  link: string
  news_date: string
  is_event: boolean
  image_url: string | null
  rsvp_enabled: boolean
  seats: string
  starts_at: string
  ends_at: string
  is_app_update: boolean
  published: boolean
}

function emptyForm(): FormState {
  return {
    title: '',
    description: '',
    link: '',
    news_date: new Date().toISOString().slice(0, 10),
    is_event: false,
    image_url: null,
    rsvp_enabled: false,
    seats: '',
    starts_at: '',
    ends_at: '',
    is_app_update: false,
    published: true,
  }
}

// datetime-local inputs use "YYYY-MM-DDTHH:MM"; strip any stored seconds.
function toLocalInput(dt: string | null | undefined): string {
  return dt ? dt.slice(0, 16) : ''
}

export function NewsManager({
  apiUrl,
  getToken,
  t,
  scope,
  gymId,
  renderAttendees,
  renderHeader,
}: NewsManagerProps) {
  const { isOpen, onOpen, onClose, onOpenChange } = useDisclosure()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [items, setItems] = useState<NewsItem[]>([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm())
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [attendeesOpenId, setAttendeesOpenId] = useState<string | null>(null)

  const listUrl = scope === 'gym' ? `${apiUrl}/news/${gymId}` : `${apiUrl}/admin/news`
  const writeBase = scope === 'gym' ? `${apiUrl}/news` : `${apiUrl}/admin/news`

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const token = await getToken()
      const res = await fetch(listUrl, { headers: { Authorization: `Bearer ${token}` } })
      if (!res.ok) throw new Error()
      const data = (await res.json()) as NewsItem[] | { gym: NewsItem[] }
      setItems(Array.isArray(data) ? data : data.gym)
    } catch {
      setError(t('news.errorLoad'))
    } finally {
      setLoading(false)
    }
  }, [listUrl, getToken, t])

  useEffect(() => {
    void load()
  }, [load])

  function openCreate() {
    setEditingId(null)
    setForm(emptyForm())
    setError(null)
    onOpen()
  }

  function openEdit(item: NewsItem) {
    setEditingId(item.id)
    setForm({
      title: item.title,
      description: item.description,
      link: item.link ?? '',
      news_date: item.news_date,
      is_event: item.is_event,
      image_url: item.image_url,
      rsvp_enabled: item.rsvp_enabled ?? false,
      seats: item.seats != null ? String(item.seats) : '',
      starts_at: toLocalInput(item.starts_at),
      ends_at: toLocalInput(item.ends_at),
      is_app_update: item.is_app_update ?? false,
      published: item.published ?? true,
    })
    setError(null)
    onOpen()
  }

  async function handleImagePick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setUploading(true)
    setError(null)
    try {
      const compressed = await compressImage(file, MAX_IMAGE_BYTES)
      const token = await getToken()
      const body = new FormData()
      body.append('file', compressed)
      const res = await fetch(`${apiUrl}/news-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body,
      })
      if (!res.ok) throw new Error()
      const { url } = (await res.json()) as { url: string }
      setForm((f) => ({ ...f, image_url: url }))
    } catch {
      setError(t('news.errorImage'))
    } finally {
      setUploading(false)
    }
  }

  async function handleSave() {
    // For events the start datetime is the source of truth; otherwise the date.
    const newsDate = form.is_event ? form.starts_at.slice(0, 10) : form.news_date
    if (!form.title.trim() || !form.description.trim() || !newsDate) {
      setError(t('news.errorRequired'))
      return
    }
    setSaving(true)
    setError(null)
    try {
      const token = await getToken()
      const seatsNum = form.seats.trim() === '' ? null : Number(form.seats)
      const rsvp = form.is_event && scope === 'gym' && form.rsvp_enabled
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        link: form.link.trim() || null,
        news_date: newsDate,
        is_event: form.is_event,
        image_url: form.image_url,
        rsvp_enabled: rsvp,
        seats: rsvp ? seatsNum : null,
        starts_at: form.is_event ? form.starts_at || null : null,
        ends_at: form.is_event ? form.ends_at || null : null,
        // Only global news carries the app-update flag; the worker ignores it otherwise.
        is_app_update: scope === 'global' && form.is_app_update,
        published: form.published,
      }
      const res = await fetch(editingId ? `${writeBase}/${editingId}` : writeBase, {
        method: editingId ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error()
      onClose()
      await load()
    } catch {
      setError(t('news.errorSave'))
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm(t('news.confirmDelete'))) return
    try {
      const token = await getToken()
      const res = await fetch(`${writeBase}/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error()
      setItems((prev) => prev.filter((i) => i.id !== id))
    } catch {
      setError(t('news.errorSave'))
    }
  }

  return (
    <div className="p-4">
      {renderHeader ? (
        renderHeader(openCreate)
      ) : (
        <div className="mb-6 flex items-center justify-between gap-2">
          <h1 className="text-xl font-bold">{t('news.managePageTitle')}</h1>
          <Button color="primary" onPress={openCreate}>
            {t('news.addButton')}
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Spinner color="primary" />
        </div>
      ) : items.length === 0 ? (
        <p className="py-12 text-center text-sm text-foreground/50">{t('news.manageEmpty')}</p>
      ) : (
        <div className="flex flex-col gap-3">
          {items.map((item) => (
            <Card key={item.id}>
              <CardBody className="py-4">
                <div className="flex items-start gap-4">
                  {item.image_url && (
                    <img
                      src={item.image_url}
                      alt=""
                      className="h-16 w-16 shrink-0 rounded-md object-cover"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    {/* The date stays on one line; chips wrap under it when narrow. */}
                    <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                      <p className="whitespace-nowrap text-xs text-foreground/40">
                        {dayFirst(item.news_date)}
                      </p>
                      {item.is_event && (
                        <span className="shrink-0 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase leading-none text-white">
                          {t('news.eventBadge')}
                        </span>
                      )}
                      {item.is_app_update && (
                        <span className="shrink-0 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold uppercase leading-none text-white">
                          {t('news.appUpdateBadge')}
                        </span>
                      )}
                      {item.published === false && (
                        <span className="shrink-0 rounded-full bg-default-300 px-2 py-0.5 text-[10px] font-bold uppercase leading-none text-foreground">
                          {t('news.draftBadge')}
                        </span>
                      )}
                    </div>
                    <p className="font-semibold">{item.title}</p>
                    <p className="line-clamp-2 text-sm text-foreground/70">{item.description}</p>
                    {item.is_event && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground/50">
                        {formatEventRange(item.starts_at, item.ends_at) && (
                          <span>🕑 {formatEventRange(item.starts_at, item.ends_at)}</span>
                        )}
                        {scope === 'gym' && item.rsvp_enabled && (
                          <>
                            <span>
                              {item.seats != null
                                ? t('news.joinedSeats', {
                                    count: item.signup_count ?? 0,
                                    seats: item.seats,
                                  })
                                : t('news.joinedCount', { count: item.signup_count ?? 0 })}
                            </span>
                            {renderAttendees && (
                              <Button
                                size="sm"
                                variant="light"
                                className="h-auto min-w-0 px-1 py-0"
                                onPress={() =>
                                  setAttendeesOpenId((id) => (id === item.id ? null : item.id))
                                }
                              >
                                {attendeesOpenId === item.id
                                  ? t('news.hideAttendees')
                                  : t('news.viewAttendees')}
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {/* Below the content, so a narrow card keeps its full width for text. */}
                <div className="mt-3 flex gap-2">
                  <Button size="sm" variant="flat" onPress={() => openEdit(item)}>
                    {t('news.edit')}
                  </Button>
                  <Button
                    size="sm"
                    variant="flat"
                    color="danger"
                    onPress={() => void handleDelete(item.id)}
                  >
                    {t('news.delete')}
                  </Button>
                </div>
                {attendeesOpenId === item.id && renderAttendees && (
                  <div className="mt-3 border-t border-divider pt-3">
                    {renderAttendees(item.id)}
                  </div>
                )}
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      <Modal isOpen={isOpen} onOpenChange={onOpenChange} scrollBehavior="inside">
        <ModalContent>
          {() => (
            <>
              <ModalHeader>{editingId ? t('news.editTitle') : t('news.createTitle')}</ModalHeader>
              <ModalBody className="gap-4">
                <Input
                  label={t('news.fieldTitle')}
                  value={form.title}
                  onValueChange={(v) => setForm((f) => ({ ...f, title: v }))}
                  isRequired
                />
                <Textarea
                  label={t('news.fieldDescription')}
                  value={form.description}
                  onValueChange={(v) => setForm((f) => ({ ...f, description: v }))}
                  minRows={3}
                  isRequired
                />
                {!form.is_event && (
                  <DateField
                    label={t('news.fieldDate')}
                    value={form.news_date}
                    onValueChange={(v) => setForm((f) => ({ ...f, news_date: v }))}
                    isRequired
                  />
                )}
                <Input
                  label={t('news.fieldLink')}
                  placeholder="https://"
                  value={form.link}
                  onValueChange={(v) => setForm((f) => ({ ...f, link: v }))}
                />
                <div>
                  <p className="mb-1.5 text-sm text-foreground/70">{t('news.fieldImage')}</p>
                  <div className="flex items-center gap-3">
                    {form.image_url && (
                      <img
                        src={form.image_url}
                        alt=""
                        className="h-16 w-16 rounded-md object-cover"
                      />
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={(e) => void handleImagePick(e)}
                    />
                    <Button
                      size="sm"
                      variant="flat"
                      isLoading={uploading}
                      onPress={() => fileInputRef.current?.click()}
                    >
                      {form.image_url ? t('news.replaceImage') : t('news.uploadImage')}
                    </Button>
                    {form.image_url && (
                      <Button
                        size="sm"
                        variant="light"
                        color="danger"
                        onPress={() => setForm((f) => ({ ...f, image_url: null }))}
                      >
                        {t('news.removeImage')}
                      </Button>
                    )}
                  </div>
                </div>
                {/* A draft stays hidden from members until this is switched on. */}
                <Switch
                  isSelected={form.published}
                  onValueChange={(v) => setForm((f) => ({ ...f, published: v }))}
                >
                  {form.published ? t('news.fieldPublished') : t('news.fieldDraft')}
                </Switch>
                <Switch
                  isSelected={form.is_event}
                  onValueChange={(v) => setForm((f) => ({ ...f, is_event: v }))}
                >
                  {t('news.fieldIsEvent')}
                </Switch>
                {/* Global news is community news by default; this marks the ones that
                    are Structural release notes. */}
                {scope === 'global' && (
                  <Switch
                    isSelected={form.is_app_update}
                    onValueChange={(v) => setForm((f) => ({ ...f, is_app_update: v }))}
                  >
                    {t('news.fieldAppUpdate')}
                  </Switch>
                )}
                {form.is_event && (
                  <div className="flex flex-col gap-5 rounded-medium bg-content2 p-4">
                    <DateField
                      withTime
                      label={t('news.fieldStartAt')}
                      value={form.starts_at}
                      onValueChange={(v) => setForm((f) => ({ ...f, starts_at: v }))}
                      isRequired
                    />
                    <DateField
                      withTime
                      label={t('news.fieldEndAt')}
                      value={form.ends_at}
                      onValueChange={(v) => setForm((f) => ({ ...f, ends_at: v }))}
                    />
                    {scope === 'gym' && (
                      <>
                        <Switch
                          isSelected={form.rsvp_enabled}
                          onValueChange={(v) => setForm((f) => ({ ...f, rsvp_enabled: v }))}
                        >
                          {t('news.fieldRsvp')}
                        </Switch>
                        {form.rsvp_enabled && (
                          <Input
                            type="number"
                            min={0}
                            label={t('news.fieldSeats')}
                            labelPlacement="outside"
                            placeholder={t('news.seatsPlaceholder')}
                            value={form.seats}
                            onValueChange={(v) => setForm((f) => ({ ...f, seats: v }))}
                          />
                        )}
                      </>
                    )}
                  </div>
                )}
                {error && <p className="text-sm text-danger">{error}</p>}
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onPress={onClose} isDisabled={saving}>
                  {t('news.cancel')}
                </Button>
                <Button color="primary" onPress={() => void handleSave()} isLoading={saving}>
                  {t('news.save')}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  )
}
